import React from "react";

/**
 * Animated Crown-Ring Logo
 * - Pure SVG (no images) with lightweight CSS animations
 * - Strokes draw in, then fills fade
 * - Diamond gently bobs + sparkles on a loop
 *
 * Props
 *  - size: overall pixel size (width). Height scales with viewBox
 *  - primary: darker gold for center ring + diamond
 *  - secondary: lighter gold for side loops
 *  - bg: background color for the SVG canvas (use transparent to blend)
 */
export default function AnimatedCrownRingLogo({
  size = 260,
  primary = "#BF8417",
  secondary = "#E8C06B",
  bg = "transparent",
}: {
  size?: number;
  primary?: string;
  secondary?: string;
  bg?: string;
}) {
  const w = size;
  // Maintain aspect ratio similar to the reference
  const h = Math.round(size * (150 / 240)); // viewBox is 240x150

  return (
    <div className="w-full flex items-center justify-center p-4">
      <svg
        width={w}
        height={h}
        viewBox="0 0 240 150"
        xmlns="http://www.w3.org/2000/svg"
        aria-labelledby="crownRingTitle"
        role="img"
        style={{ background: bg }}
        className="drop-shadow-sm"
      >
        <title id="crownRingTitle">Animated Crown-Ring Logo</title>

        {/* --- Styles for animation --- */}
        <style>{`
          .draw {
            stroke-dasharray: 1000;
            stroke-dashoffset: 1000;
            animation: dash 1.6s ease forwards;
          }
          .draw.delay { animation-delay: .25s; }
          .draw.delay2 { animation-delay: .5s; }
          .fade-in { opacity: 0; animation: fade .9s ease forwards .9s; }
          .bob { animation: bob 2.8s ease-in-out infinite; transform-origin: 120px 24px; }
          .sparkle { opacity: 0; animation: sparkle 2.8s ease-in-out infinite; transform-origin: 120px 24px; }
          .hover-glow:hover { filter: drop-shadow(0 0 10px rgba(0,0,0,.25)) drop-shadow(0 0 18px currentColor); }

          @keyframes dash { to { stroke-dashoffset: 0; } }
          @keyframes fade { to { opacity: 1; } }
          @keyframes bob { 0%,100%{ transform: translateY(0); } 50%{ transform: translateY(-2.5px); } }
          @keyframes sparkle {
            0%, 30% { opacity: 0; transform: scale(.6) rotate(0deg); }
            40% { opacity: .9; transform: scale(1) rotate(20deg); }
            55% { opacity: 0; transform: scale(.6) rotate(40deg); }
            100% { opacity: 0; }
          }
        `}</style>

        {/* --- Side loops (light gold) --- */}
        {/* Left loop */}
        <ellipse
          cx="96"
          cy="96"
          rx="60"
          ry="34"
          transform="rotate(-18 96 96)"
          fill="none"
          stroke={secondary}
          strokeWidth="10"
          strokeLinecap="round"
          className="draw hover-glow"
        />
        {/* Right loop */}
        <ellipse
          cx="144"
          cy="96"
          rx="60"
          ry="34"
          transform="rotate(18 144 96)"
          fill="none"
          stroke={secondary}
          strokeWidth="10"
          strokeLinecap="round"
          className="draw delay hover-glow"
        />

        {/* --- Center vertical ring (dark gold) --- */}
        <ellipse
          cx="120"
          cy="80"
          rx="28"
          ry="48"
          fill="none"
          stroke={primary}
          strokeWidth="10"
          strokeLinecap="round"
          className="draw delay2"
        />

        {/* Inner crossing curves to hint the knot/overlap */}
        <path
          d="M100 83 C 108 65, 132 65, 140 83"
          fill="none"
          stroke={primary}
          strokeWidth="6"
          strokeLinecap="round"
          className="draw delay2"
        />
        <path
          d="M100 95 C 108 113, 132 113, 140 95"
          fill="none"
          stroke={primary}
          strokeWidth="6"
          strokeLinecap="round"
          className="draw delay2"
        />

        {/* --- Diamond atop the ring --- */}
        <g className="bob">
          <polygon
            points="120,10 134,24 120,38 106,24"
            fill={primary}
            className="fade-in hover-glow"
          />
          {/* Tiny sparkle */}
          <path
            d="M120 6 l3 3 -3 3 -3-3 3-3z"
            fill={primary}
            className="sparkle"
          />
        </g>
      </svg>
    </div>
  );
}
